import { _ as _applyDecoratedDescriptor } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("{{! BeebeeModalModern Component }}\n{{!\n  Componente modal riutilizzabile e moderno con slot per personalizzazione.\n\n  Parametri:\n  @isOpen - booleano per mostrare/nascondere la modal\n  @title - titolo della modal\n  @size - dimensione della modal (small, medium, large, xlarge)\n  @onClose - callback per chiudere la modal\n  @closeOnBackdrop - permette chiusura cliccando fuori (default: true)\n  @showCloseButton - mostra il pulsante X di chiusura (default: true)\n  @isLoading - mostra stato di caricamento (default: false)\n  @zIndex - z-index della modal (default: 1000)\n  @headerClass - classe CSS aggiuntiva per l\'header\n  @bodyClass - classe CSS aggiuntiva per il body\n  @footerClass - classe CSS aggiuntiva per il footer\n}}\n\n{{! Portal. The overlay is `position: fixed`, and a fixed box is laid out\n    against the nearest ancestor that carries a `transform` — not the\n    viewport. The calendar event popup gets one from Popper (translate3d on\n    desktop) and from the mobile bottom-sheet rule, and it clips at\n    `overflow: hidden`: a modal opened from inside it (the recurrence\n    dialog) was confined to the popup\'s 130px and its third button fell off\n    the box (POP-383). Rendering under <body> takes every modal out of any\n    transformed container. Tests keep finding it: the destination is the\n    test root when one exists (test-helpers scope queries to it). }}\n{{#if @isOpen}}\n  {{#in-element this.modalDestination insertBefore=null}}\n  <div\n    class=\'beebee-modal-modern-overlay {{if @isOpen \"show\"}} {{if @alert \"alert-modal\"}}\'\n    style=\'z-index: {{this.zIndex}}\'\n    tabindex=\'-1\'\n    data-test-handle-keydown\n    ...attributes\n    {{on \'click\' this.handleBackdropClick}}\n    {{on \'keydown\' this.handleKeydown}}\n  >\n    <div\n      class=\'beebee-modal-modern-dialog {{@dialogClass}}\'\n      style=\'max-width: {{this.maxWidth}}\'\n      {{on \'click\' this.stopPropagation}}\n    >\n      {{#if (or @title this.showCloseButton)}}\n        <div class=\'beebee-modal-modern-header {{@headerClass}}\'>\n          <h4>\n            {{#if @title}}\n              {{@title}}\n            {{/if}}\n          </h4>\n          {{#if this.showCloseButton}}\n            <button\n              type=\'button\'\n              class=\'beebee-modal-modern-close-button hdr-btn\'\n              aria-label={{t \'close\'}}\n              disabled={{this.busy}}\n              data-test-close-modal\n              {{on \'click\' this.closeModal}}\n            >\n              <FaIcon @icon=\'times\' aria-hidden=\'true\' />\n              <EmberTooltip @popperContainer=\'body\'>\n                {{t \'close\'}}\n              </EmberTooltip>\n            </button>\n          {{/if}}\n        </div>\n      {{/if}}\n\n      <div class=\'beebee-modal-modern-body {{@bodyClass}}\'>\n        {{#if this.isLoading}}\n          <div class=\'beebee-modal-modern-loading\'>\n            <div class=\'spinner\'></div>\n            <span>\n              {{or @loadingText \'Caricamento...\'}}\n            </span>\n          </div>\n        {{else}}\n          {{yield to=\'main\'}}\n        {{/if}}\n      </div>\n\n      {{#if (has-block \'footer\')}}\n        <div class=\'beebee-modal-modern-footer {{@footerClass}}\'>{{yield to=\'footer\'}}</div>\n      {{/if}}\n    </div>\n  </div>\n  {{/in-element}}\n{{/if}}");

var _class;
let BbModalModernComponent = (_class = class BbModalModernComponent extends Component {
  get isOpen() {
    return this.args.isOpen ?? false;
  }
  get title() {
    return this.args.title ?? '';
  }
  get size() {
    return this.args.size ?? 'medium';
  }
  get onClose() {
    return this.args.onClose ?? (() => {});
  }
  get closeOnBackdrop() {
    return this.args.closeOnBackdrop ?? true;
  }
  get showCloseButton() {
    return this.args.showCloseButton ?? true;
  }
  get isLoading() {
    return this.args.isLoading ?? false;
  }

  /**
   * When truthy, the modal refuses to close: the header ✕, a backdrop click and
   * the Escape key — all routed through `closeModal` — become no-ops. Pass
   * `@busy={{this.someSaveTask.isRunning}}`
   * so the modal stays mounted for the WHOLE task. Closing mid-task unmounts the
   * body, and ember-concurrency then auto-cancels the still-running task at its
   * next yield — silently dropping the second half of the save. The consumer's
   * save button keeps its own loading/disabled state; this keeps the container
   * alive around it.
   * @returns {boolean}
   */
  get busy() {
    return this.args.busy ?? false;
  }
  get loadingText() {
    return this.args.loadingText ?? 'Caricamento...';
  }
  get zIndex() {
    return this.args.zIndex ?? 1000;
  }

  /**
   * Where the overlay is rendered (see the template comment): under <body>,
   * so no transformed/clipping ancestor can confine a `position: fixed`
   * overlay. In a test run `#ember-testing` is preferred because
   * `@ember/test-helpers` scopes `find`/`click` to that root.
   * @returns {Element}
   */
  get modalDestination() {
    return document.getElementById('ember-testing') ?? document.body;
  }
  get footerButtons() {
    return this.args.footerButtons ?? [];
  }
  get headerClass() {
    return this.args.headerClass ?? '';
  }
  get bodyClass() {
    return this.args.bodyClass ?? '';
  }
  get footerClass() {
    return this.args.footerClass ?? '';
  }
  get dialogClass() {
    return this.args.dialogClass ?? '';
  }
  get maxWidth() {
    switch (this.size) {
      case 'small':
        return '300px';
      case 'medium':
        return '500px';
      case 'large':
        return '800px';
      case 'xlarge':
        return '1140px';
      default:
        return '500px';
    }
  }
  closeModal() {
    // A task launched from inside the modal is still running (see `busy`).
    // Every close path — ✕, backdrop, Escape — funnels through here, so this
    // single guard keeps the container mounted until the task finishes.
    if (this.busy) {
      return;
    }
    if (this.onClose) {
      this.onClose();
    }
  }
  handleBackdropClick() {
    if (this.closeOnBackdrop) {
      this.closeModal();
    }
  }
  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.closeModal();
    }
  }
  stopPropagation(event) {
    event.stopPropagation();
  }
}, _applyDecoratedDescriptor(_class.prototype, "closeModal", [action], Object.getOwnPropertyDescriptor(_class.prototype, "closeModal"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleBackdropClick", [action], Object.getOwnPropertyDescriptor(_class.prototype, "handleBackdropClick"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleKeydown", [action], Object.getOwnPropertyDescriptor(_class.prototype, "handleKeydown"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "stopPropagation", [action], Object.getOwnPropertyDescriptor(_class.prototype, "stopPropagation"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, BbModalModernComponent);

export { BbModalModernComponent as default };
//# sourceMappingURL=bb-modal-modern.js.map
